import { Box, Divider, Flex, HStack, Input, InputGroup, InputLeftElement, Spinner, Text, useBreakpointValue } from '@chakra-ui/react'
import { MapPin } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import CountrySelectWithFlag from './selects/CountrySelectWithFlag'
import InvalidInput from './forms/InvalidInput'

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY

/** Parsed address components from a Google Places result */
export interface AddressFields {
  address: string
  city: string
  postcode: string
  country: string // 2-char ISO code
}

interface AddressAutocompleteProps {
  address: string
  city: string
  postcode: string
  country: string
  onChange: (fields: Partial<AddressFields>) => void
  isDisabled?: boolean
  /** "sm" = 32px (drawers), "md" = 40px (modals/pages) */
  size?: 'sm' | 'md'
  isCityInvalid?: boolean
  cityInvalidText?: string
}

interface Prediction {
  placeId: string
  main: string
  secondary: string
}

/* ------------------------------------------------------------------ */
/*  Load the Google Maps JS API exactly once                          */
/* ------------------------------------------------------------------ */
let _loadPromise: Promise<void> | null = null

function loadGoogleMaps(): Promise<void> {
  if (window.google?.maps?.places) return Promise.resolve()
  if (_loadPromise) return _loadPromise

  _loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Google Maps'))
    document.head.appendChild(script)
  })

  return _loadPromise
}

/* ------------------------------------------------------------------ */
/*  Extract structured address parts from a PlaceResult               */
/* ------------------------------------------------------------------ */
function extractAddressFields(place: google.maps.places.PlaceResult): AddressFields {
  const components = place.address_components ?? []

  const get = (type: string, short = false) => {
    const comp = components.find((c) => c.types.includes(type))
    return comp ? (short ? comp.short_name : comp.long_name) : ''
  }

  const streetNumber = get('street_number')
  const route = get('route')
  const street = [route, streetNumber].filter(Boolean).join(' ')

  return {
    address: street || place.formatted_address?.split(',')[0] || '',
    city: get('locality') || get('postal_town') || get('administrative_area_level_2'),
    postcode: get('postal_code'),
    country: get('country', true),
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */
const AddressAutocomplete = ({
  address,
  city,
  postcode,
  country,
  onChange,
  isDisabled = false,
  size = 'md',
  isCityInvalid = false,
  cityInvalidText,
}: AddressAutocompleteProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null)
  const placesService = useRef<google.maps.places.PlacesService | null>(null)
  const dummyDiv = useRef<HTMLDivElement | null>(null)

  const [ready, setReady] = useState(!!window.google?.maps?.places)
  const [isOpen, setIsOpen] = useState(false)
  const [predictions, setPredictions] = useState<Prediction[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Load Google Maps
  useEffect(() => {
    loadGoogleMaps().then(() => setReady(true))
  }, [])

  // Init services once ready
  useEffect(() => {
    if (!ready) return
    if (!autocompleteService.current) {
      autocompleteService.current = new google.maps.places.AutocompleteService()
    }
    if (!placesService.current) {
      if (!dummyDiv.current) {
        dummyDiv.current = document.createElement('div')
      }
      placesService.current = new google.maps.places.PlacesService(dummyDiv.current)
    }
  }, [ready])

  // Click-outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Fetch predictions (debounced)
  const fetchPredictions = useCallback(
    (input: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current)

      if (!input || input.length < 2 || !autocompleteService.current) {
        setPredictions([])
        setIsSearching(false)
        return
      }

      setIsSearching(true)
      debounceRef.current = setTimeout(() => {
        autocompleteService.current!.getPlacePredictions(
          {
            input,
            types: ['address'],
            ...(country ? { componentRestrictions: { country: country.toLowerCase() } } : {}),
          },
          (results, status) => {
            setIsSearching(false)
            if (status === google.maps.places.PlacesServiceStatus.OK && results) {
              setPredictions(
                results.map((r) => ({
                  placeId: r.place_id,
                  main: r.structured_formatting.main_text,
                  secondary: r.structured_formatting.secondary_text,
                }))
              )
              setActiveIndex(-1)
            } else {
              setPredictions([])
            }
          }
        )
      }, 250)
    },
    [country]
  )

  // Select a prediction → fetch Place Details → fill fields
  const selectPrediction = useCallback(
    (prediction: Prediction) => {
      if (!placesService.current) return

      placesService.current.getDetails(
        { placeId: prediction.placeId, fields: ['address_components', 'formatted_address'] },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            const fields = extractAddressFields(place)
            onChange(fields)
          }
        }
      )

      setIsOpen(false)
      setPredictions([])
    },
    [onChange]
  )

  // Keyboard nav
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || predictions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => (i < predictions.length - 1 ? i + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => (i > 0 ? i - 1 : predictions.length - 1))
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault()
      selectPrediction(predictions[activeIndex])
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false })
  const inputHeight = isMobile ? '44px' : size === 'sm' ? '32px' : '40px'
  const fontSize = isMobile ? '16px' : '14px'

  const labelProps =
    size === 'sm'
      ? { color: 'neutral.500', textStyle: 'b-xs' as const, mb: '4px' }
      : { textStyle: 'b-sm' as const, fontWeight: '400', color: 'neutral.900', mb: '4px' }

  const handleCountryChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => onChange({ country: e.target.value }),
    [onChange]
  )

  return (
    <Box>
      {/* Address field with custom autocomplete dropdown */}
      <Box mb="8px" position="relative" ref={containerRef}>
        <Text {...labelProps}>Address</Text>
        <InputGroup>
          <InputLeftElement h={inputHeight} pointerEvents="none">
            <MapPin size={14} color="#A0AEC0" />
          </InputLeftElement>
          <Input
            ref={inputRef}
            height={inputHeight}
            fontSize={fontSize}
            pl="36px"
            placeholder="Start typing an address..."
            value={address}
            onChange={(e) => {
              onChange({ address: e.target.value })
              fetchPredictions(e.target.value)
              setIsOpen(true)
            }}
            onFocus={() => {
              if (address.length >= 2) {
                fetchPredictions(address)
                setIsOpen(true)
              }
            }}
            onKeyDown={handleKeyDown}
            isDisabled={isDisabled}
            autoComplete="off"
          />
        </InputGroup>

        {/* Custom dropdown */}
        {isOpen && (isSearching || predictions.length > 0) && (
          <Box
            position="absolute"
            w="100%"
            zIndex="10000"
            top="100%"
            mt="4px"
            bg="white"
            py="4px"
            boxShadow="0px 6px 35px 5px #0000001F"
            borderColor="neutral.100"
            borderWidth="1px"
            borderRadius="8px"
            maxH="260px"
            overflow="hidden"
          >
            {isSearching && predictions.length === 0 && (
              <Flex h="48px" align="center" justify="center">
                <Spinner size="sm" color="primary.500" mr="8px" />
                <Text textStyle="b-sm" color="neutral.500">
                  Searching...
                </Text>
              </Flex>
            )}

            <Box
              maxH="248px"
              overflowY="auto"
              sx={{
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: 'neutral.200', borderRadius: '2px' },
                '&::-webkit-scrollbar-thumb:hover': { background: 'neutral.300' },
              }}
            >
              {predictions.map((p, i) => (
                <Box key={p.placeId}>
                  <Flex
                    px="12px"
                    h="48px"
                    cursor="pointer"
                    alignItems="center"
                    transition="0.15s ease-out"
                    py="8px"
                    bg={activeIndex === i ? 'neutral.50' : 'transparent'}
                    _hover={{ bg: 'neutral.50' }}
                    onClick={() => selectPrediction(p)}
                    onMouseEnter={() => setActiveIndex(i)}
                  >
                    <Flex
                      align="center"
                      justify="center"
                      w="32px"
                      h="32px"
                      borderRadius="8px"
                      bg="neutral.50"
                      flexShrink={0}
                    >
                      <MapPin size={15} color="#718096" />
                    </Flex>
                    <Box ml="10px" overflow="hidden">
                      <Text noOfLines={1} textStyle="b-sm" color="neutral.900" fontWeight="500">
                        {p.main}
                      </Text>
                      <Text noOfLines={1} textStyle="b-xs" color="neutral.400">
                        {p.secondary}
                      </Text>
                    </Box>
                  </Flex>
                  {i < predictions.length - 1 && <Divider m="0" />}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* City + Postcode row */}
      <HStack spacing="8px" mb="8px">
        <Box flex="1">
          <Text {...labelProps}>City</Text>
          <Input
            height={inputHeight}
            fontSize={fontSize}
            aria-label="City"
            placeholder="City"
            value={city}
            onChange={(e) => onChange({ city: e.target.value })}
            isDisabled={isDisabled}
          />
          <InvalidInput isError={isCityInvalid} label="city" override={cityInvalidText} />
        </Box>
        <Box flex="1">
          <Text {...labelProps}>Postcode</Text>
          <Input
            height={inputHeight}
            fontSize={fontSize}
            aria-label="Postcode"
            placeholder="Postcode"
            value={postcode}
            onChange={(e) => onChange({ postcode: e.target.value })}
            isDisabled={isDisabled}
          />
        </Box>
      </HStack>

      {/* Country */}
      <Box>
        <Text {...labelProps}>Country</Text>
        <CountrySelectWithFlag
          value={country}
          onChange={handleCountryChange}
          name="country"
          isDisabled={isDisabled}
          height={inputHeight}
          mb="0px"
        />
      </Box>
    </Box>
  )
}

export default AddressAutocomplete
